import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useEffect, useState, useRef } from "react";
import { Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { FormInput } from "../components/custom/components";
import UserLayout from "../components/custom/layouts/UserLayout";
import { camelize, rupeeFormat } from "../config/functions";
import { AuthUserType } from "../redux/actions/authUserActions";
import {
    ClientRegistrationType,
    fetchClientRegistrationAction,
} from "../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../redux/store";
import db, { IndexDBProduct, refreshProductsFromAPI } from "../db/db";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { editStockMasterAction, fetchStockMasterAction, StockMasterType } from "../redux/actions/stockMasterAction";
import { getStockMaster, getStockMasterById } from "../services/stockMaster.service";

interface StockBodyProps {
    sno: number;
    id: number;
    productId?: number;
    productCode?: string;
    batchNumber?: number;
    productName?: string;
    modelNumber?: string;
    hsnCode?: string;
    gst?: number;
    igst?: number;
    cgst?: number;
    sgst?: number;
    expiryDate?: string;
    pwoGst?: number;
    pwGst?: number;
    wholesaleRate?: number;
    retailRate?: number;
    mrp?: number;
    stock?: number;
    department?: string;
    category?: string;
    brand?: string;
    updatedByOn?: string;
    isEditing?: boolean;
    onEditClick: (id: number) => void;
    onFieldChange: (id: number, field: keyof IndexDBProduct, value: string | number) => void;
    onSave: (id: number) => void;
}

/** Min width for money/qty inputs so full values stay visible on narrow screens (table scrolls horizontally). */
const numericEditStyle: React.CSSProperties = { minWidth: "6.25rem", width: "100%" };
const vendorEditStyle: React.CSSProperties = { minWidth: "8.5rem", width: "100%" };

const StockBody: React.FC<StockBodyProps> = ({
    sno,
    id,
    productName,
    productCode,
    batchNumber,
    modelNumber,
    category,
    brand,
    stock,
    pwoGst,
    pwGst,
    wholesaleRate,
    retailRate,
    mrp,
    hsnCode,
    gst,
    updatedByOn,
    isEditing,
    onEditClick,
    onFieldChange,
    onSave,
}) => {
    return (
        <tr>
            <td className="align-middle text-center text-nowrap">{sno}</td>
            <td className="text-capitalize align-middle" style={{ minWidth: "6.5rem" }}>{category}</td>
            <td className="text-capitalize align-middle" style={{ minWidth: "6rem" }}>{brand}</td>
            <td className="text-capitalize align-middle" style={{ minWidth: "9rem" }}>{productName}</td>
            <td className="text-capitalize align-middle" style={{ minWidth: "3rem" }}>{modelNumber}</td>
            <td className="text-capitalize align-middle text-nowrap">{batchNumber}</td>
            <td className="text-capitalize align-middle text-nowrap">{hsnCode}</td>
            <td className="text-capitalize align-middle text-center text-nowrap">{gst}</td>

            {/* PWO GST */}
            <td className="align-middle text-end text-nowrap" style={{ minWidth: "6.75rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="pwoGst"
                        step="0.01"
                        value={pwoGst}
                        onChange={(e) => onFieldChange(id, 'pwoGst', parseFloat(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-end"
                        style={numericEditStyle}
                    />
                ) : (
                    rupeeFormat(pwoGst)
                )}
            </td>

            {/* PW GST */}
            <td className="align-middle text-end text-nowrap" style={{ minWidth: "6.75rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="pwGst"
                        step="0.01"
                        value={pwGst}
                        onChange={(e) => onFieldChange(id, 'pwGst', parseFloat(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-end"
                        style={numericEditStyle}
                    />
                ) : (
                    rupeeFormat(pwGst)
                )}
            </td>

            {/* Wholesale Rate */}
            <td className="align-middle text-end text-nowrap" style={{ minWidth: "6.75rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="wholesaleRate"
                        step="0.01"
                        value={wholesaleRate}
                        onChange={(e) => onFieldChange(id, 'wholesaleRate', parseFloat(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-end"
                        style={numericEditStyle}
                    />
                ) : (
                    <span className="d-inline-block w-100 text-end">{wholesaleRate}</span>
                )}
            </td>

            {/* Retail Rate */}
            <td className="align-middle text-end text-nowrap" style={{ minWidth: "6.75rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="retailRate"
                        step="0.01"
                        value={retailRate}
                        onChange={(e) => onFieldChange(id, 'retailRate', parseFloat(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-end"
                        style={numericEditStyle}
                    />
                ) : (
                    <span className="d-inline-block w-100 text-end">{retailRate}</span>
                )}
            </td>

            {/* MRP */}
            <td className="align-middle text-end text-nowrap" style={{ minWidth: "6.75rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="mrp"
                        step="0.01"
                        value={mrp}
                        onChange={(e) => onFieldChange(id, 'mrp', parseFloat(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-end"
                        style={numericEditStyle}
                    />
                ) : (
                    rupeeFormat(mrp)
                )}
            </td>

            {/* Stock */}
            <td className="align-middle text-center text-nowrap" style={{ minWidth: "5.5rem" }}>
                {isEditing ? (
                    <FormInput
                        type="number"
                        name="stock"
                        step="1"
                        value={stock}
                        onChange={(e) => onFieldChange(id, 'stock', parseInt(e.target.value) || 0)}
                        containerClass="m-0"
                        className="form-control-sm text-center"
                        style={{ minWidth: "4.5rem", width: "100%" }}
                    />
                ) : (stock || 0) < 50 ? (
                    <Badge bg="danger" className="badge">
                        {stock} / 50
                    </Badge>
                ) : (
                    <Badge bg="success" className="badge">
                        {stock}
                    </Badge>
                )}
            </td>

            {/* Vendor */}
            {/* <td className="text-capitalize align-middle" style={{ minWidth: "1rem" }}> */}
                {/* {vendor}
            </td> */}

            {/* Updated */}
            <td className="align-middle small text-nowrap" style={{ minWidth: "9.5rem" }} title={updatedByOn}>
                {updatedByOn}
            </td>

            {/* Action */}
            <td className="text-center align-middle text-nowrap" style={{ minWidth: "3.25rem" }}>
                {isEditing ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={() => onSave(Number(id))}
                        title="Save"
                    >
                        <i className="fe-check"></i>
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-sm btn-info text-white"
                        onClick={() => onEditClick(id)}
                        title="Edit"
                    >
                        <i className="fe-edit"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

const StockMaster = () => {
    const toast = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const [stockList, setStockList] = useState<IndexDBProduct[]>([]);
    const [filteredStockList, setFilteredStockList] = useState<IndexDBProduct[]>([]);
    const [editableStockList, setEditableStockList] = useState<IndexDBProduct[]>([]);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [fullRecords, setFullRecords] = useState<Record<number, any>>({});
    const clients = useSelector<StoreState, ClientRegistrationType[]>(
        (state) => state.clientRegistration
    );

    const AuthUser = useSelector<StoreState, AuthUserType | null>(
        (state) => state.authUser
    );

    const url = window.location.pathname;
    const rolePermissions = AuthUser?.roles?.find((r) => r.pageUrl === url);

    const searchRef = useRef<HTMLInputElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get("search") || "";

    const normalizeProducts = (response: IndexDBProduct[]) =>
        response.map((p: any) => ({
            id: p.id,
            productId: p.productId,
            productName: p.productName ?? "",
            productCode: p.productCode ?? "",
            modelNumber:p.modelNumber ?? "",
            batchNumber: p.batchNumber ?? "",
            sac: p.sac ?? p.hsnCode ?? "",
            gst: p.gst != null ? p.gst : 0,
            productDetails: p.productDetails,
            pwoGst: typeof p.pwoGst === "string" ? parseFloat(p.pwoGst) || 0 : p.pwoGst ?? 0,
            pwGst: typeof p.pwGst === "string" ? parseFloat(p.pwGst) || 0 : p.pwGst ?? 0,
            wholesaleRate: typeof p.wholesaleRate === "string" ? parseFloat(p.wholesaleRate) || 0 : p.wholesaleRate ?? 0,
            retailRate: typeof p.retailRate === "string" ? parseFloat(p.retailRate) || 0 : p.retailRate ?? 0,
            mrp: typeof p.mrp === "string" ? parseFloat(p.mrp) || 0 : p.mrp ?? 0,
            stock: typeof p.stock === "string" ? parseInt(p.stock, 10) || 0 : p.stock ?? 0,
            department: p.department ?? "",
            category: p.category ?? "",
            brand: p.brand ?? "",
            vendor: p.vendor,
            updatedByOn: p.updatedByOn ?? "",
            hsnCode: p.hsnCode ?? p.sac ?? "",
        }));

    useEffect(() => {
        setEditableStockList([...filteredStockList]);
    }, [filteredStockList]);
    
    useEffect(() => {
        refreshProductsFromAPI();
    }, [])

    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        const loadProducts = async () => {
            try {
                const updatedData = await db.products.toArray();
                const normalized = normalizeProducts(updatedData);
                setStockList(normalized);
            } catch (err) {
                console.error("Error fetching products:", err);
                toast('Failed to fetch products', ToastTypes.ERROR);
            }
        };
        loadProducts();
        intervalId = setInterval(loadProducts, 10 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);


    useEffect(() => {
        const filterProducts = (products: IndexDBProduct[], term: string) => {
            if (!term) return products;
            const regex = new RegExp(term.trim(), "gi");
            return products.filter(
                (stockMaster) =>
                    regex.test(stockMaster.productName || '') ||
                    regex.test(stockMaster.productCode || '') ||
                    regex.test(stockMaster.modelNumber?.toString()|| "")||
                    regex.test(stockMaster.hsnCode?.toString() || "") ||
                    regex.test(stockMaster.brand?.toString() || "") ||
                    regex.test(stockMaster.category?.toString() || "") ||
                    regex.test(stockMaster.stock?.toString() || "") ||
                    regex.test(stockMaster.gst?.toString() || "")
            );
        };
        const filtered = filterProducts(stockList, searchTerm);
        setFilteredStockList(filtered);
    }, [stockList, searchTerm]);

    const searchTermChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(window.location.search);
        if (value) {
            params.set("search", value);
        } else {
            params.delete("search");
        }
        setSearchParams(params);
    };

    const handleFieldChange = (id: number, field: keyof IndexDBProduct, value: string | number) => {
        setEditableStockList((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    const handleEditClick = async (id: number) => {
        if (fullRecords[id]) {
            setEditingRowId(id);
            return;
        }

        try {
            const result = (await getStockMasterById(id));
            
            if (result.data) {
                setFullRecords(prev => ({ ...prev, [id]: result.data.data }));
                setEditingRowId(id);
            } else {
                toast("Failed to load full product data", ToastTypes.ERROR);
            }
        } catch (err) {
            console.error("Fetch full record error:", err);
            toast("Could not load full product data", ToastTypes.ERROR);
        }
    };

    const saveRow = async (id: number) => {
        const row = editableStockList.find(r => r.id === id);
        const fullRecord = fullRecords[id];

        if (!row || !fullRecord) {
            toast("Missing data to save", ToastTypes.ERROR);
            return;
        }

        try {
            const payload = {
                ...fullRecord,
                productCode: row.productCode,
                productGroupCode: row.productCode,
                batchNumber: row.batchNumber || 1,
                expiryDate: "",
                categoryId: row.category,
                attribute1: "",
                attribute2: "",
                attribute3: "",
                attribute4: "",
                attribute5: "",
                freeQuantity: 0,
                purchaseRate: 0,
                freightCost: 0,
                landingCost: 0,
                marginPercent: 0,
                marginAmt: 0,
                salesRate: 0,
                lRate: 0,
                taxableAmount: 0,
                igstAmount: 0,
                cgstAmount: 0,
                sgstAmount: 0,
                totalAmount: 0,
                unitProfit: 0,
                createdBy: "",
                createdAt: row.updatedByOn,
                updatedBy: "",
                updatedAt: row.updatedByOn,
                modelNumber: row.modelNumber,
                productName: row.productName,
                sac: row.hsnCode,
                hsnCode: row.hsnCode,
                gst: String(row.gst),
                priceWogst: String(row.pwoGst),
                priceGst: String(row.pwGst),
                wRate: String(row.wholesaleRate),
                rRate: String(row.retailRate),
                mrp: String(row.mrp),
                quantity: String(row.stock),
                totalQuantity: String(row.stock),
                department: row.department,
                category: row.category,
                brandId: row.brand,
            };

            const message = await dispatch(editStockMasterAction(payload, id));
            toast(message, ToastTypes.SUCCESS);

            await db.products.update(id, {
                ...row,
                updatedByOn: new Date().toLocaleString(),
            });

            // dispatch(fetchStockMasterAction()); direct api service call
            // const response = await getStockMaster()
            // const data = response.data.data?.map((res: any) => {
            //     let camelCase: Partial<StockMasterType> = {}
            //     for (let i = 0; i < Object.keys(res).length; i++) {
            //         const key = camelize(Object.keys(res)[i])
            //         const value = Object.values(res)[i]
            //         camelCase = { ...camelCase, [key]: value }
            //     }
            //     return camelCase as StockMasterType
            // })
            setEditingRowId(null);
        } catch (err: any) {
            toast(err?.message || "Failed to save", ToastTypes.ERROR);
        }
    };

    const downloadPDF = () => {
        const logoUrl = clients[0]?.shopPhoto;
        const doc = new jsPDF("landscape");
        const marginX = 10;
        const boxWidth = doc.internal.pageSize.width - 2 * marginX;
        const boxHeight = 35;
        let startY = 15;

        doc.rect(marginX, startY - 5, boxWidth, boxHeight);

        if (logoUrl) {
            doc.addImage(logoUrl, "JPEG", marginX + 5, startY, 30, 25);
        }

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const textX = marginX + 40;
        doc.text(clients[0]?.shopFullName || "", textX, startY + 5);
        doc.setFont("helvetica", "normal");
        doc.text(
            `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${clients[0]?.district || ""}`,
            textX,
            startY + 10
        );
        doc.text(
            `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${clients[0]?.contactNo1 || ""}`,
            textX,
            startY + 15
        );
        doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 20);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        const heading = "Current Stock Report";
        const dateTime = new Date().toLocaleDateString("en-GB");
        const time = new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
        const headingWidth = doc.getTextWidth(heading);
        const pageWidth = doc.internal.pageSize.width;
        doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 15);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        const dateTimeText = `${dateTime} | ${time}`;
        const dateTimeWidth = doc.getTextWidth(dateTimeText);
        doc.text(dateTimeText, marginX + boxWidth - dateTimeWidth - 10, startY + 20);

        startY += 45;

        const groupedData = filteredStockList.reduce(
            (acc, item) => {
                const category = item.category || "Uncategorized";
                if (!acc[category]) acc[category] = [];
                acc[category].push(item);
                return acc;
            },
            {} as Record<string, any[]>
        );

        Object.keys(groupedData).forEach((category) => {
            const categoryData = groupedData[category];
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`${category} :`, marginX, startY);
            startY += 5;

            const tableBody = categoryData.map((item, index) => [
                index + 1,
                item.brand || "N/A",
                item.productName || "N/A",
                item.modelNumber,
                item.hsnCode,
                parseFloat(String(item.gst)) || 0,
                rupeeFormat(item.pwGst),
                rupeeFormat(item.retailRate),
                rupeeFormat(item.mrp),
                item.stock,
            ]);

            autoTable(doc, {
                startY,
                head: [["S.No", "Brand", "Product Name", "Part Number", "HSN Code", "GST %", "PW GST", "Retail", "MRP", "Stock"]],
                body: tableBody,
                theme: "grid",
                styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
                headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: "bold", lineColor: 0, lineWidth: 0.1 },
                columnStyles: {
                    0: { halign: "center" },
                    1: { halign: "left" },
                    2: { halign: "left" },
                    3: { halign: "left" },
                    4: { halign: "left" },
                    5: { halign: "left" },
                    6: { halign: "right" },
                    7: { halign: "right" },
                    8: { halign: "right" },
                    9: { halign: "center" },
                },
                margin: { top: 10, left: marginX, right: marginX },
            });

            startY = (doc as any).autoTable.previous.finalY + 10;

            if (startY + 20 > doc.internal.pageSize.height) {
                doc.addPage();
                startY = 15;
            }
        });

        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageHeight = doc.internal.pageSize.height;
            const footerY = pageHeight - 10;
            doc.setFontSize(10);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
        }

        doc.save("stock_master_report.pdf");
    };

    useEffect(() => {
        if (searchTerm !== "") {
            searchRef.current?.focus();
        }
    }, [searchTerm]);

    useEffect(() => {
        dispatch(fetchClientRegistrationAction());
    }, [dispatch]);

    return (
        <UserLayout>
            <h4>Stock Master</h4>
            <div className="row">
                <div className="col">
                    {rolePermissions?.view ? (
                        <div className="card p-2 w-100">
                            <div className="row">
                                <div className="col-8 ps-3">
                                    <FormInput
                                        onChange={searchTermChangeHandler}
                                        name=""
                                        placeholder="Search by product name, product code, no. of stock and HSN code..."
                                        value={searchTerm}
                                        onFocus={() => {
                                            const mainContentRef = document.querySelector('.table-wrapper');
                                            if (mainContentRef) {
                                                mainContentRef.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }
                                        }}
                                        innerRef={searchRef}
                                        className="container my-2 py-1 fs-4"
                                        style={{ position: "sticky", top: "3px" }}
                                    />
                                </div>
                                <div className="col-4 text-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary p-2 border-rounded fw-bold"
                                        onClick={downloadPDF}
                                    >
                                        <i className="fe-download"></i> Download PDF
                                    </button>
                                </div>
                            </div>

                            <p className="small text-muted mb-1 d-md-none px-1">
                                Swipe horizontally to see all columns when editing prices or stock.
                            </p>

                            <div
                                className="table-responsive stock-master-table-scroll rounded border"
                                style={{ position: "relative", WebkitOverflowScrolling: "touch" }}
                            >
                                <table className="table table-sm table-hover align-middle mb-0" style={{ minWidth: "1180px" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center text-nowrap">S.No</th>
                                            <th className="text-nowrap">Category</th>
                                            <th className="text-nowrap">Brand</th>
                                            <th className="text-nowrap">Product Name</th>
                                            <th className="text-nowrap">Part No</th>
                                            <th className="text-nowrap">Batch No</th>
                                            <th className="text-nowrap">HSN Code</th>
                                            <th className="text-center text-nowrap">GST %</th>
                                            <th className="text-end text-nowrap">PWO GST</th>
                                            <th className="text-end text-nowrap">PW GST</th>
                                            <th className="text-end text-nowrap">W.Sale Rate</th>
                                            <th className="text-end text-nowrap">Retail Rate</th>
                                            <th className="text-end text-nowrap">MRP</th>
                                            <th className="text-center text-nowrap">Stock</th>
                                            {/* <th className="text-nowrap">Vendor</th> */}
                                            <th className="text-nowrap">Updated By / On</th>
                                            <th className="text-center text-nowrap">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editableStockList.map((sl, i) => (
                                            <StockBody
                                                key={sl.id}
                                                sno={i + 1}
                                                id={sl.id || 0}
                                                productName={sl.productName || ''}
                                                modelNumber={sl.modelNumber || ''}
                                                batchNumber={sl.batchNumber || 0}
                                                category={sl.category || "N/A"}
                                                brand={sl.brand || "N/A"}
                                                stock={sl.stock || 0}
                                                pwoGst={sl.pwoGst || 0}
                                                pwGst={sl.pwGst || 0}
                                                wholesaleRate={sl.wholesaleRate || 0}
                                                retailRate={sl.retailRate || 0}
                                                mrp={sl.mrp || 0}
                                                hsnCode={sl.hsnCode || ''}
                                                gst={parseFloat(String(sl.gst)) || 0}
                                                // vendor={sl.vendor || "N/A"}
                                                updatedByOn={sl.updatedByOn || ''}
                                                isEditing={editingRowId === sl.id}
                                                onEditClick={handleEditClick}
                                                onFieldChange={handleFieldChange}
                                                onSave={saveRow}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="card p-2 w-100">
                            <h3 className="text-secondary text-center">Oops! No Records.</h3>
                        </div>
                    )}
                </div>
            </div>
        </UserLayout>
    );
};

export default StockMaster;